// components/entities/pages/ProjectPage.tsx
import { useEffect } from 'react';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import InpaintCanvas from '@/components/InpaintCanvas';
import { projectSceneOf } from '#lib/modelist/projectScene';
import { pauseTimelines } from '#lib/workspace-runtime-playback-state';

// The scene carries three.js: loaded only when it is opened.
const Fater3DScene = dynamic(() => import('@/components/fater3d/Fater3DView').then((module) => module.Fater3DScene), { ssr: false });

// paper.js is browser-only and Vector-only: loaded when a Vector project opens, never on the server.
const VectorEditor = dynamic(() => import('@/components/vector/VectorEditor'), { ssr: false });

interface ProjectPageProps {
  projectId: string;
  className?: string;
  hasEntityRowAbove?: boolean;
}

export const ProjectPage = ({ projectId, className }: ProjectPageProps) => {
  const { kernel } = useKernel();
  // Switched from the top bar's scene button. Neither surface unmounts for the other: the one not shown is hidden,
  // inert, as the Portal keeps Factory; the scene only once opened, so three.js stays out until then.
  const scene = useKernelStore((state) => projectSceneOf(state, projectId));
  const sceneShown = scene === 'shown';

  // No timeline plays under the scene: the canvas stays mounted there, so nothing else would stop one.
  useEffect(() => {
    if (sceneShown && kernel.current) pauseTimelines(kernel.current, kernel.current.getInventory().Project.registry[projectId]?.workspaceIds ?? []);
  }, [kernel, projectId, sceneShown]);
  const isVector = useKernelStore((state) => state.inventory.Project.registry[projectId]?.projectType === 'VECTOR');

  useEffect(() => {
    if (!kernel.current || !projectId) return;
    void (async () => {
      try {
        await kernel.current?.syncEntity('Project', projectId);
        if(!kernel.current?.readProjectRuntimeState(projectId).isProjectLoading){
          await kernel.current?.loadProject(projectId);
        }
      } catch (error) {
        console.error('[ProjectPage] Failed to sync/load project.', { projectId, error });
      }
    })();
  }, [kernel, projectId]);

  return (
    <div className={clsx('relative h-full w-full min-h-[620px] overflow-hidden', className)}>
      {scene ? (
        <div inert={!sceneShown} style={{ display: sceneShown ? 'contents' : 'none' }}>
          <Fater3DScene projectId={projectId} />
        </div>
      ) : null}
      <div inert={sceneShown} style={{ display: sceneShown ? 'none' : 'contents' }}>
        {isVector ? (
          <VectorEditor projectId={projectId} />
        ) : (
          <InpaintCanvas
            kernel={kernel}
            projectId={projectId}
          />
        )}
      </div>
    </div>
  );
};

